import React, { useState, useEffect } from 'react';
import AnimalCard from '../components/AnimalCard';
import { animaux, zones } from '../data/zooData';

const AnimauxPage: React.FC = () => {
  const [selectedZone, setSelectedZone] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAnimaux, setFilteredAnimaux] = useState(animaux);
  const [isLoading, setIsLoading] = useState(true);

  // Effet d'animation au chargement
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);

  // Filtre des animaux selon la zone et la recherche
  useEffect(() => {
    let filtered = animaux;
    
    if (selectedZone !== null) {
      filtered = filtered.filter(animal => animal.zoneId === selectedZone);
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        animal => 
          animal.nom.toLowerCase().includes(search) || 
          animal.espece.toLowerCase().includes(search)
      );
    }
    
    setFilteredAnimaux(filtered);
  }, [selectedZone, searchTerm]);

  return (
    <div className="min-h-screen">
      {/* Hero réduit pour la page des animaux */}
      <div 
        className="h-80 relative flex items-center justify-center"
        style={{ 
          backgroundImage: `url(https://images.unsplash.com/photo-1592754862816-1a21a4ea2281?w=1920&auto=format&fit=crop)`,
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Nos animaux
          </h1>
          <p className="text-xl text-white max-w-3xl mx-auto">
            Découvrez la diversité extraordinaire de notre faune et apprenez-en plus sur nos pensionnaires
          </p>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-green-800 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <button 
                className={`px-4 py-2 rounded-full font-medium transition-colors ${selectedZone === null ? 'bg-yellow-500 text-white' : 'bg-white text-green-800 hover:bg-green-100'}`}
                onClick={() => setSelectedZone(null)}
              >
                Tous
              </button>
              {zones.map(zone => (
                <button 
                  key={zone.id}
                  className={`px-4 py-2 rounded-full font-medium transition-colors ${selectedZone === zone.id ? 'bg-yellow-500 text-white' : 'bg-white text-green-800 hover:bg-green-100'}`}
                  onClick={() => setSelectedZone(zone.id)}
                >
                  {zone.nom}
                </button>
              ))}
            </div>
            
            <div className="relative">
              <input 
                type="text"
                placeholder="Rechercher un animal..."
                className="bg-white rounded-full py-2 pl-10 pr-4 w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des animaux */}
      <div className="container mx-auto px-4 py-12">
        {isLoading ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600"></div>
          </div>
        ) : filteredAnimaux.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAnimaux.map((animal, index) => (
              <div 
                key={animal.id}
                className={`
                  opacity-0 animate-fadeIn
                `}
                style={{
                  animationDelay: `${index * 0.1}s`,
                  animationFillMode: 'forwards'
                }}
              >
                <AnimalCard animal={animal} />
              </div>
            ))}
          </div>
        ) : (
          <div className="min-h-[60vh] flex flex-col items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-2xl font-semibold text-gray-600">Aucun animal ne correspond à votre recherche</h2>
            <p className="text-gray-500 mt-2">Essayez d'autres critères de recherche ou consultez toutes les zones</p>
            <button 
              className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors"
              onClick={() => {
                setSelectedZone(null);
                setSearchTerm('');
              }}
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>
      
      {/* Section informative */}
      <div className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-green-900 mb-6 text-center">Découvrez nos animaux</h2>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <p className="text-gray-600 mb-6">
                Le Zoo Aventure abrite plus de 150 espèces différentes provenant des quatre coins du monde. Nos équipes de soigneurs et vétérinaires passionnés veillent quotidiennement au bien-être de nos pensionnaires.
              </p>
              <p className="text-gray-600 mb-6">
                Pour une expérience encore plus enrichissante, n'hésitez pas à participer à nos nourrissages commentés, qui ont lieu plusieurs fois par jour dans différentes zones du parc.
              </p>
              <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                <h3 className="font-bold text-green-800 mb-2">Le saviez-vous ?</h3>
                <p className="text-gray-700">
                  Plus de 30% des espèces présentes dans notre zoo font partie de programmes internationaux de conservation pour les espèces menacées. Votre visite contribue directement à ces efforts de préservation !
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS pour l'animation */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.5s ease-out;
          }
        `}
      </style>
    </div>
  );
};

export default AnimauxPage;
